import m from 'mithril'

// Real-time WebSocket client with auto-reconnect.
// Connects to the project's Durable Object via /api/projects/:projectId/ws

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000]
const PING_INTERVAL = 30000

export function createRealtimeClient() {
  let ws = null
  let projectId = null
  let reconnectAttempt = 0
  let reconnectTimer = null
  let pingTimer = null
  let listeners = new Map() // type → Set<fn>
  let presence = [] // current presence list
  let connected = false

  function getWsUrl(pid) {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${proto}//${location.host}/api/projects/${pid}/ws`
  }

  function connect(pid) {
    if (ws) disconnect()
    projectId = pid

    try {
      ws = new WebSocket(getWsUrl(pid))
    } catch {
      scheduleReconnect()
      return
    }

    ws.onopen = () => {
      connected = true
      reconnectAttempt = 0
      startPing()
      emit('connected', {})
      m.redraw()
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'pong') return
        if (data.type === 'presence') {
          presence = data.clients || []
          emit('presence', { clients: presence })
          m.redraw()
          return
        }
        emit(data.type, data)
        m.redraw()
      } catch { /* ignore bad messages */ }
    }

    ws.onclose = () => {
      connected = false
      stopPing()
      emit('disconnected', {})
      scheduleReconnect()
      m.redraw()
    }

    ws.onerror = () => {
      // onclose will fire after this
    }
  }

  function disconnect() {
    clearTimeout(reconnectTimer)
    stopPing()
    reconnectTimer = null
    if (ws) {
      ws.onclose = null // prevent reconnect
      ws.close()
      ws = null
    }
    connected = false
    presence = []
    projectId = null
    reconnectAttempt = 0
  }

  function scheduleReconnect() {
    if (!projectId) return
    const delay = RECONNECT_DELAYS[Math.min(reconnectAttempt, RECONNECT_DELAYS.length - 1)]
    reconnectAttempt++
    reconnectTimer = setTimeout(() => {
      if (projectId) connect(projectId)
    }, delay)
  }

  function startPing() {
    stopPing()
    pingTimer = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }))
      }
    }, PING_INTERVAL)
  }

  function stopPing() {
    clearInterval(pingTimer)
    pingTimer = null
  }

  function send(data) {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data))
    }
  }

  function on(type, fn) {
    if (!listeners.has(type)) listeners.set(type, new Set())
    listeners.get(type).add(fn)
    return () => listeners.get(type)?.delete(fn) // unsubscribe fn
  }

  function emit(type, data) {
    const fns = listeners.get(type)
    if (fns) for (const fn of fns) fn(data)
  }

  // Tell server which session we're viewing (for presence)
  function setViewing(sessionId) {
    send({ type: 'presence', sessionId })
  }

  return {
    connect,
    disconnect,
    send,
    on,
    setViewing,
    get connected() { return connected },
    get presence() { return presence },
  }
}

// Singleton instance
export const realtime = createRealtimeClient()
